/**
 * Line tracing utilities for path drawing.
 * Uses Bresenham's line algorithm with orthogonal conversion for grid-based paths.
 */

import { PathPoint, pointsEqual } from "../types/path";

/**
 * Bresenham's line algorithm - generates all points on a line between two points.
 * This can produce diagonal steps which need to be converted to orthogonal for our grid.
 */
export function bresenhamLine(from: PathPoint, to: PathPoint): PathPoint[] {
  const points: PathPoint[] = [];

  let x0 = from.x;
  let y0 = from.y;
  const x1 = to.x;
  const y1 = to.y;

  const dx = Math.abs(x1 - x0);
  const dy = Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1;
  const sy = y0 < y1 ? 1 : -1;
  let err = dx - dy;

  while (true) {
    points.push({ x: x0, y: y0 });

    if (x0 === x1 && y0 === y1) break;

    const e2 = 2 * err;
    if (e2 > -dy) {
      err -= dy;
      x0 += sx;
    }
    if (e2 < dx) {
      err += dx;
      y0 += sy;
    }
  }

  return points;
}

/**
 * Convert a Bresenham line (which may have diagonal steps) to orthogonal-only steps.
 * This ensures all adjacent points are cardinally adjacent (up/down/left/right).
 *
 * When a diagonal step is detected, we insert an intermediate point.
 * We prefer horizontal-first movement (x change before y change).
 */
export function bresenhamToOrthogonal(from: PathPoint, to: PathPoint): PathPoint[] {
  const rawLine = bresenhamLine(from, to);

  if (rawLine.length <= 1) {
    return rawLine;
  }

  const result: PathPoint[] = [rawLine[0]];

  for (let i = 1; i < rawLine.length; i++) {
    const prev = result[result.length - 1];
    const curr = rawLine[i];

    const dx = curr.x - prev.x;
    const dy = curr.y - prev.y;

    // If it's a diagonal step, insert intermediate point(s)
    if (dx !== 0 && dy !== 0) {
      // Horizontal first, then vertical
      result.push({ x: curr.x, y: prev.y });
    }

    result.push(curr);
  }

  return result;
}

/**
 * Get all intermediate cells between two points on the grid.
 * Returns points in order from (but not including) `from` to (and including) `to`.
 * Uses orthogonal-only movement (no diagonals).
 */
export function getIntermediateCells(from: PathPoint, to: PathPoint): PathPoint[] {
  if (pointsEqual(from, to)) {
    return [];
  }

  const allPoints = bresenhamToOrthogonal(from, to);

  // Remove the starting point, keep the rest
  return allPoints.slice(1);
}

/**
 * Check if two points are orthogonally adjacent (not diagonal).
 */
export function areAdjacent(a: PathPoint, b: PathPoint): boolean {
  const dx = Math.abs(a.x - b.x);
  const dy = Math.abs(a.y - b.y);
  return (dx === 1 && dy === 0) || (dx === 0 && dy === 1);
}

/**
 * Check if a point is within grid bounds.
 */
export function isInBounds(point: PathPoint, gridSize: number): boolean {
  return point.x >= 0 && point.x < gridSize && point.y >= 0 && point.y < gridSize;
}

/**
 * Get all orthogonal neighbors of a point.
 */
export function getNeighbors(point: PathPoint): PathPoint[] {
  return [
    { x: point.x, y: point.y - 1 }, // up
    { x: point.x, y: point.y + 1 }, // down
    { x: point.x - 1, y: point.y }, // left
    { x: point.x + 1, y: point.y }, // right
  ];
}

/**
 * Get the Manhattan distance between two points.
 */
export function manhattanDistance(a: PathPoint, b: PathPoint): number {
  return Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
}
